import { inject } from '@angular/core';
import { Observable } from 'rxjs';
import { BankEntity, ClubEntity, MemberEntity, SharingInfoEntity, UserEntity, ValidatedIBAN } from '@core/entities';
import { DomSanitizer } from '@angular/platform-browser';
import { GetDatabaseId, GetMemberId, GetTeamId } from '@core/utils';
import { instanceToPlain, plainToClass, plainToInstance } from 'class-transformer';
import { map } from 'rxjs/operators';
import { ImageConversionService, UserService } from '@core/services';
import { MemberService } from '@core/services/member.service';

export class UseMyProfile {
  private readonly _userService = inject(UserService);
  private readonly _memberService = inject(MemberService);
  private readonly _sanitizer = inject(DomSanitizer);
  private readonly _imageConversionService = inject(ImageConversionService);

  // ----------- User Photo -------------
  getUserPhoto(memberId: number) {
    return this._userService.getUserProfilePhotoFromLocal(memberId).pipe(
      map((resp) => {
        if (resp) {
          const image = this._imageConversionService.convertBase64ToBlobUrl(resp);
          return this._sanitizer.bypassSecurityTrustUrl(image);
        }
        return null;
      }),
    );
  }

  getUserProfile(): Observable<UserEntity> {
    return this._userService.getInfo(+GetMemberId(), +GetTeamId(), +GetDatabaseId()).pipe(
      map((response: UserEntity) => {
        if (response) {
          return response;
        }
        return null;
      }),
    );
  }

  getMemberProfile(): Observable<MemberEntity> {
    return this._memberService.get(+GetMemberId(), +GetTeamId(), +GetDatabaseId()).pipe(
      map((response: MemberEntity) => {
        if (response) {
          return plainToClass(MemberEntity, response);
        }
        return null;
      }),
    );
  }

  getClubInfo(): Observable<ClubEntity> {
    return this._memberService.getClub(+GetTeamId(), +GetDatabaseId()).pipe(
      map((response: ClubEntity) => {
        if (response) {
          return plainToClass(ClubEntity, response);
        }
        return null;
      }),
    );
  }

  getBirthdayAdvertisementSharing(): Observable<any> {
    return this._memberService.getSharingInfo().pipe(
      map((response: SharingInfoEntity) => {
        if (response) {
          return plainToClass(SharingInfoEntity, response);
        }
        return null;
      }),
    );
  }

  saveUserMemberProfile(data: Partial<MemberEntity>): Observable<any> {
    const tData = instanceToPlain(data);
    return this._userService.save(+GetMemberId(), +GetTeamId(), +GetDatabaseId(), tData);
  }

  saveSharingBirthday(shareBirthday: 0 | 1): Observable<any> {
    const body = {
      shareBirthday,
    };
    return this._memberService.updateBirthdaySharing(body);
  }

  saveAdvertisementView(allowAdvertis: 0 | 1): Observable<any> {
    const body = {
      allowAdvertis,
    };
    return this._memberService.updateAdvertisementView(body);
  }

  saveBankInfo(data: BankEntity): Observable<any> {
    const body = {
      ...data,
      member_id: GetMemberId(),
      club_id: GetTeamId(),
      database_id: GetDatabaseId(),
    };

    return this._memberService.updateBank(body);
  }

  changeUserPassword(data: any): Observable<any> {
    return this._userService.updatePassword(data);
  }

  validateIBAN(iban: string): Observable<Partial<ValidatedIBAN>> {
    return this._memberService.validateIban(iban).pipe(
      map((response) => {
        if (typeof response === 'object' && response.iban) {
          return plainToInstance(ValidatedIBAN, response) as ValidatedIBAN;
        }

        return {
          isValid: false,
          message: response,
        } as Partial<ValidatedIBAN>;
      }),
    );
  }

  // ----------- AUX -------------

  convertBase64ToImage(image: string) {
    if (!image) {
      return null;
    }
    const base64Data = image.substring(20);
    return this._sanitizer.bypassSecurityTrustUrl(this._imageConversionService.convertBase64ToBlobUrl(base64Data)) as string;
  }
}
